import React, { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';

import { Button } from '../../../../components/custom-essentials';
import { DateRangeSelector, SelectSingle, FormikControl, Switch, checkResponses } from '../../../../components/form';
import { formatDateApi } from '../../../../components/functions';
import { TooltipWrapper } from '../../../../components/display';
import { CSVExport } from '../../../../components/export';
import { appendContractBalances } from './functions';
import ContractsTable from './contracts/ContractsTable';
import MathPlexContractModal from '../../../../components/modal/contracts/MathPlexContractModal';
import ContractModal from '../contracts/ContractModal';

import {
    fetchContractsDaterangeCenter,
    fetchStudentsAll,
    createContract,
    updateContract,
    deleteContract
} from '../../../../actions';

const start = new Date();
start.setMonth(start.getMonth() - 6);
const startApi = formatDateApi(start);
const end = new Date();
end.setMonth(end.getMonth() + 6);
const endApi = formatDateApi(end);

// Portal detection
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isReadingPlex = hostname.includes('readingplex');

const filterTypeOptions = [
    { value: 'all', label: 'All' },
    { value: 'studentName', label: 'Student Name' },
    { value: 'type', label: 'Type' },
    { value: 'notes', label: 'Notes' },
];

function Contracts(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });
    const formRef = useRef();

    const { centerOptions, loading, setLoading, setNContracts,
        fetchContractsDaterangeCenter, fetchStudentsAll,
        createContract, updateContract, deleteContract } = props;

    const [hasLoaded, setHasLoaded] = useState(false);
    const [apiError, setApiError] = useState(false);
    // Data
    const [contracts, setContracts] = useState([]);
    const [students, setStudents] = useState([]);
    const [filteredContracts, setFilteredContracts] = useState([]);
    const [monthlyExpected, setMonthlyExpected] = useState(0);
    // Form
    const [drsValid, setDrsValid] = useState(true);
    // Modal state (same approach as ReadingPlex Contracts page)
    const [showContractModal, setShowContractModal] = useState(false);
    const [contractModalMode, setContractModalMode] = useState('create');
    const [selectedContract, setSelectedContract] = useState(null);

    const filterContracts = useCallback((unfilteredContracts, filterType, filterQuery, currentOnly,
        activeStudentsOnly, extra, overUnderOnly, monthlyOnly, newStudents) => {
        filterQuery = filterQuery.replace(/ /g, '').toLowerCase();
        const checkStudentNames = (c) => c.studentName.replace(/ /g, '').toLowerCase().includes(filterQuery);
        const checkType = (c) => c.type.toLowerCase().includes(filterQuery);
        const checkNotes = (c) => c.notes.toLowerCase().includes(filterQuery);

        let newFilteredContracts = unfilteredContracts.filter(c => {
            switch (filterType.value) {
                case 'all':
                    return checkStudentNames(c) || checkType(c) || checkNotes(c);
                case 'studentName':
                    return checkStudentNames(c);
                case 'type':
                    return checkType(c);
                case 'notes':
                    return checkNotes(c);
                default:
                    return false;
            }
        });

        if (currentOnly && currentOnly !== 'false') {
            // Filter to unexpired contracts: no end_date OR end_date after today
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            newFilteredContracts = newFilteredContracts.filter(c => {
                if (!c.end_date) return true; // Never expires / no set end date
                const endDate = new Date(c.end_date);
                if (isNaN(endDate.getTime())) return true; // If API returns invalid date, keep it visible
                return endDate >= today; // End date is today or later
            });
        }
        if (activeStudentsOnly && activeStudentsOnly !== 'false') {
            const studentMap = {};
            const activeField = isReadingPlex ? 'rp_active' : 'mp_active';
            newStudents.forEach(s => studentMap[s.user_id] = parseInt(s[activeField]) === 1 ? true : false);

            newFilteredContracts = newFilteredContracts.filter(c => studentMap[c.student]);
        }
        if (!extra || extra === 'false') {
            newFilteredContracts = newFilteredContracts.filter(c => c.type !== 'Extra');
        }
        if (overUnderOnly && overUnderOnly !== 'false') {
            newFilteredContracts = newFilteredContracts.filter(c => c.balanceOwed !== c.balancePaid);
        }
        if (monthlyOnly && monthlyOnly !== 'false') {
            newFilteredContracts = newFilteredContracts.filter(c => c.payment_frequency === 'Monthly');
        }

        const normalizedContracts = newFilteredContracts.map(c => {
            const hasBalancePaid = c.balancePaid !== undefined && c.balancePaid !== null;
            const hasBalanceOwed = c.balanceOwed !== undefined && c.balanceOwed !== null;
            const balancePaid = Number(c.balancePaid ?? 0);
            const balanceOwed = Number(c.balanceOwed ?? 0);
            const balanceDiff = balancePaid - balanceOwed;

            let paymentStatusLabel = 'Matching';
            if (!hasBalancePaid && !hasBalanceOwed) paymentStatusLabel = 'Unknown';
            else if (balanceDiff < 0) paymentStatusLabel = `Under (${Math.abs(balanceDiff)})`;
            else if (balanceDiff > 0) paymentStatusLabel = `Over (${Math.abs(balanceDiff)})`;

            return {
                ...c,
                balancePaid,
                balanceOwed,
                paymentStatus: paymentStatusLabel
            };
        });

        let newMonthlyExpected = 0;
        normalizedContracts.forEach(c => {
            if (c.payment_frequency === 'Monthly' && c.type !== 'Extra') {
                newMonthlyExpected += parseInt(c.amount_per_payment);
            }
        });
        setMonthlyExpected(newMonthlyExpected);

        if (mounted.current) {
            setFilteredContracts(normalizedContracts);
            setHasLoaded(true);
        }
    }, [mounted]);


    const refreshData = useCallback(() => {
        (async function refresh() {
            if (loading.contracts || !drsValid || !formRef.current?.values) return;
            if (mounted.current) setLoading({ ...loading, contracts: true });
            const { startDate, endDate, selectedCenter, filterType, filterQuery, currentOnly, activeStudentsOnly,
                extra, overUnderOnly, monthlyOnly } = formRef.current.values;

            // Get contracts and students
            const contractsRes = await fetchContractsDaterangeCenter({
                startDate: startDate,
                endDate: endDate,
                center: selectedCenter.value
            });
            const studentsRes = await fetchStudentsAll();
            const isApiError = checkResponses(contractsRes, studentsRes);
            if (isApiError) {
                if (mounted.current) {
                    setApiError('Error fetching data from the server. Please try again later.');
                    setLoading({ ...loading, contracts: false });
                    setHasLoaded(true);
                }
                return;
            } else setApiError(false);

            const newContracts = contractsRes.data.contracts || [];
            const newPayments = contractsRes.data.payments || [];
            const newStudents = studentsRes.data || [];

            const studentToNameMap = {};
            newStudents.forEach(s => {
                s.studentName = `${s.first_name} ${s.last_name}`;
                studentToNameMap[s.user_id] = s.studentName;
            });

            const contractMap = {};
            // Portal-aware current contract field
            const currentContractField = isReadingPlex ? 'rp_current_contract' : 'mp_current_contract';
            newStudents.forEach(s => {
                const currentContractId = s[currentContractField];
                if (currentContractId && parseInt(currentContractId) !== -1) {
                    contractMap[parseInt(currentContractId)] = s.user_id;
                }
            });
            newContracts.forEach(c => {
                if (contractMap[parseInt(c.id)]) c.isCurrent = true;
                else c.isCurrent = false;
            });

            const newContractsAppended = appendContractBalances(newContracts, newPayments).map(c => {
                c.studentName = studentToNameMap[c.student] || `Unknown student (UID: ${c.student})`;
                return c;
            });

            if (mounted.current) {
                setContracts(newContractsAppended);
                setNContracts(newContractsAppended.length);
                setStudents(newStudents);
                // Pass newContractsAppended (with balances) to filterContracts, not newContracts
                filterContracts(newContractsAppended, filterType, filterQuery, currentOnly, activeStudentsOnly, extra,
                    overUnderOnly, monthlyOnly, newStudents);
                setLoading({ ...loading, contracts: false });
            }
        })()
    }, [loading, drsValid, setLoading, formRef, filterContracts, setNContracts,
        fetchContractsDaterangeCenter, fetchStudentsAll]);
    useEffect(() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Modal handlers (same approach as ReadingPlex Contracts page)
    const handleOpenContractModal = React.useCallback((mode, contract = null) => {
        setContractModalMode(mode);
        setSelectedContract(contract);
        setShowContractModal(true);
    }, []);

    const handleCloseContractModal = React.useCallback((shouldRefresh = false) => {
        setShowContractModal(false);
        setSelectedContract(null);
        if (shouldRefresh) {
            setTimeout(() => {
                refreshData();
            }, 500);
        }
    }, [refreshData]);

    return (
        <div>
            <Formik
                enableReinitialize
                initialValues={{
                    startDate: startApi,
                    endDate: endApi,
                    selectedCenter: centerOptions[0] || { value: -1, label: 'Loading centers...' },
                    filterType: { value: 'all', label: 'All' },
                    filterQuery: '',
                    currentOnly: true,
                    activeStudentsOnly: true,
                    extra: true,
                    overUnderOnly: false,
                    monthlyOnly: false,
                }}
                innerRef={formRef}
                onSubmit={refreshData}
            >
                {formik => (
                    <form onSubmit={formik.handleSubmit}>
                        <h2>Contracts ({contracts.length})</h2>
                        <br />
                        <div className="flex flex-row gap-x-4 items-center">
                            <div className="grid grid-cols-1 gap-y-2">
                                <DateRangeSelector
                                    id="contracts-drs-1"
                                    startName="startDate"
                                    endName="endDate"
                                    startLabel="Start Date"
                                    endLabel="End Date"
                                    startValue={formik.values.startDate}
                                    endValue={formik.values.endDate}
                                    defaultValid={true}
                                    onStartChange={formik.handleChange}
                                    onEndChange={formik.handleChange}
                                    onChangeValidation={setDrsValid}
                                />
                            </div>
                            <div className="grid grid-cols-1 gap-y-2 w-1/4">
                                <SelectSingle
                                    id="contracts-selectedCenter"
                                    name="selectedCenter"
                                    label="Center"
                                    value={formik.values.selectedCenter}
                                    onChange={formik.handleChange}
                                    options={centerOptions}
                                />
                            </div>
                            <div>
                                <Button
                                    color="lte-mpTeal"
                                    onClick={formik.handleSubmit}
                                >
                                    Search
                                </Button>
                            </div>
                        </div>

                        <br />

                        {apiError ? <div className="text-mpLRed">{apiError}</div> :
                            <>
                                <hr />
                                <br />

                                <h2>Filter Results ({filteredContracts.length})</h2>
                                <br />
                                <div className="flex flex-row gap-x-4 items-center">
                                    <div className="grid grid-cols-1 gap-y-2 w-1/4">
                                        <SelectSingle
                                            id="contracts-searchType"
                                            name="filterType"
                                            label="Filter by"
                                            value={formik.values.filterType}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                filterContracts(contracts, e.target.value, formik.values.filterQuery,
                                                    formik.values.currentOnly, formik.values.activeStudentsOnly,
                                                    formik.values.extra, formik.values.overUnderOnly,
                                                    formik.values.monthlyOnly, students);
                                            }}
                                            options={filterTypeOptions}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 gap-y-2 w-1/3">
                                        <FormikControl
                                            id="flag-manager-searchQuery"
                                            name="filterQuery"
                                            placeholder="Enter a filter query..."
                                            value={formik.values.filterQuery}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                filterContracts(contracts, formik.values.filterType, e.target.value,
                                                    formik.values.currentOnly, formik.values.activeStudentsOnly,
                                                    formik.values.extra, formik.values.overUnderOnly,
                                                    formik.values.monthlyOnly, students);
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <Button
                                            color="lte-mpLBlue"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleOpenContractModal('create');
                                            }}
                                        >
                                            + New Contract
                                        </Button>
                                    </div>
                                    <div className="ml-auto">
                                        <TooltipWrapper
                                            tooltipText={
                                                <div>
                                                    <div>
                                                        What gets exported?
                                                    </div>
                                                    <br />
                                                    <div>
                                                        All contracts that are currently ({filteredContracts.length} items).
                                                    </div>
                                                </div>
                                            }
                                        >
                                            <CSVExport
                                                title="Contracts"
                                                label="Export Contracts to CSV"
                                                data={filteredContracts}
                                            />
                                        </TooltipWrapper>
                                    </div>
                                </div>
                                <br />
                                <div className="flex flex-row gap-x-4 self-center">
                                    <div>
                                        <Switch
                                            name="currentOnly"
                                            label={
                                                <TooltipWrapper
                                                    tooltipText={`Only show contracts assigned as a student's "Current Contract"`}
                                                >
                                                    <div className="text-mpLBlue">Current Contracts Only</div>
                                                </TooltipWrapper>
                                            }
                                            color="mpTeal"
                                            textPosition="after"
                                            checked={formik.values.currentOnly}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                filterContracts(contracts, formik.values.filterType,
                                                    formik.values.filterQuery, e.target.value, formik.values.activeStudentsOnly,
                                                    formik.values.extra, formik.values.overUnderOnly,
                                                    formik.values.monthlyOnly, students);
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <Switch
                                            name="activeStudentsOnly"
                                            label={
                                                <TooltipWrapper
                                                    tooltipText={`Only show contracts linked to an active student account`}
                                                >
                                                    <div className="text-mpLBlue">Active Students Only</div>
                                                </TooltipWrapper>
                                            }
                                            color="mpTeal"
                                            textPosition="after"
                                            checked={formik.values.activeStudentsOnly}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                filterContracts(contracts, formik.values.filterType,
                                                    formik.values.filterQuery, formik.values.currentOnly, e.target.value,
                                                    formik.values.extra, formik.values.overUnderOnly,
                                                    formik.values.monthlyOnly, students);
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <Switch
                                            name="extra"
                                            label={
                                                <TooltipWrapper
                                                    tooltipText={`Show contracts of the "Extra" type`}
                                                >
                                                    <div className="text-mpLBlue">Show Extra</div>
                                                </TooltipWrapper>
                                            }
                                            color="mpTeal"
                                            textPosition="after"
                                            checked={formik.values.extra}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                filterContracts(contracts, formik.values.filterType,
                                                    formik.values.filterQuery, formik.values.currentOnly,
                                                    formik.values.activeStudentsOnly, e.target.value,
                                                    formik.values.overUnderOnly, formik.values.monthlyOnly, students);
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <Switch
                                            name="overUnderOnly"
                                            label={
                                                <TooltipWrapper
                                                    tooltipText={`Hide payments with the "Matching" payment status`}
                                                >
                                                    <div className="text-mpLBlue">Hide Matching</div>
                                                </TooltipWrapper>
                                            }
                                            color="mpTeal"
                                            textPosition="after"
                                            checked={formik.values.overUnderOnly}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                filterContracts(contracts, formik.values.filterType,
                                                    formik.values.filterQuery, formik.values.currentOnly,
                                                    formik.values.activeStudentsOnly, formik.values.extra,
                                                    e.target.value, formik.values.monthlyOnly, students);
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <Switch
                                            name="monthlyOnly"
                                            label={
                                                <TooltipWrapper
                                                    tooltipText={`Only display contracts with the "Monthly" payment frequency`}
                                                >
                                                    <div className="text-mpLBlue">Monthly Only</div>
                                                </TooltipWrapper>
                                            }
                                            color="mpTeal"
                                            textPosition="after"
                                            checked={formik.values.monthlyOnly}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                filterContracts(contracts, formik.values.filterType,
                                                    formik.values.filterQuery, formik.values.currentOnly,
                                                    formik.values.activeStudentsOnly, formik.values.extra,
                                                    formik.values.overUnderOnly, e.target.value, students);
                                            }}
                                        />
                                    </div>
                                </div>
                                {monthlyExpected !== 0 &&
                                    <>
                                        <br />
                                        <hr />
                                        <br />
                                        <h2>Expected Monthly Income: ${monthlyExpected}</h2>
                                    </>
                                }
                            </>
                        }

                    </form>
                )}
            </Formik>

            <br />

            <ContractsTable
                contracts={apiError ? [] : filteredContracts}
                refreshData={refreshData}
                students={students}
                createContract={createContract}
                updateContract={updateContract}
                deleteContract={deleteContract}
                onShowModal={handleOpenContractModal}
            />
            
            {showContractModal && (
                isReadingPlex ? (
                    <ContractModal
                        key={`contract-modal-${contractModalMode}-${selectedContract?.id || 'new'}`}
                        show={showContractModal}
                        mode={contractModalMode}
                        contract={selectedContract}
                        students={students}
                        onClose={handleCloseContractModal}
                        createContract={createContract}
                        updateContract={updateContract}
                        deleteContract={deleteContract}
                    />
                ) : (
                    <MathPlexContractModal
                        key={`contract-modal-${contractModalMode}-${selectedContract?.id || 'new'}`}
                        show={showContractModal}
                        mode={contractModalMode}
                        contract={selectedContract}
                        students={students}
                        onClose={handleCloseContractModal}
                        createContract={createContract}
                        updateContract={updateContract}
                        deleteContract={deleteContract}
                    />
                )
            )}
        </div>
    );
}

export default connect(null, {
    fetchContractsDaterangeCenter,
    fetchStudentsAll,
    createContract,
    updateContract,
    deleteContract
})(Contracts);